// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/installApp.ts
// 🔧 PURPOSE: "Installa l'app" (il foglio InstallAppSheet, il riquadro in
//   Home e chi decide quando proporlo, InstallAppManager).
//
//   - Android/Chrome: il browser manda `beforeinstallprompt`; lo si tiene
//     da parte (captureInstallPrompt, chiamato in index.tsx prima di React)
//     e "Installa" apre la finestra del sistema.
//   - iPhone/iPad: nessuna finestra del sistema; si spiega dove toccare,
//     con i passi giusti per Safari 26 (Condividi dentro "•••"), Safari
//     più vecchio, iPad, Chrome e le app (Instagram, Facebook…) da cui
//     bisogna prima aprire Safari.
//   - Già installata: aperta come app (standalone), o installata da qui
//     (appinstalled / scelta accettata), o Chrome che la trova
//     (getInstalledRelatedApps). Da Safari non si può sapere: c'è "L'ho
//     già installata".
//   - Quante volte: "Non ora" = 3 giorni; al massimo 3 volte da sola;
//     "L'ho già installata" = mai più. Tutto in localStorage (mai un errore).
// ============================================

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>;
}

const INSTALLED_KEY = 'pwa-installed';
const PREFS_KEY = 'nva.installPrompt.v1';
export const INSTALL_CHANGED_EVENT = 'nva:install-changed';
/** Il riquadro della Home chiede di aprire il foglio. */
export const OPEN_INSTALL_EVENT = 'nva:open-install';

export const SNOOZE_DAYS = 3;
export const MAX_AUTO_SHOWS = 3;

let deferred: BeforeInstallPromptEvent | null = null;

const emit = () => {
  try {
    window.dispatchEvent(new Event(INSTALL_CHANGED_EVENT));
  } catch {
    // niente
  }
};

const readFlag = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeFlag = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // salvataggio bloccato: vale per questa visita
  }
};

/** In index.tsx, prima di React: Chrome manda l'evento una volta sola e presto. */
export function captureInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    writeFlag(INSTALLED_KEY, 'true');
    emit();
  });
}

/** Chrome ha dato il permesso di aprire la sua finestra "Installa". */
export const canPromptInstall = (): boolean => !!deferred;

/** "Installa" su Android: la finestra del sistema. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const ev = deferred;
  if (!ev) return 'unavailable';
  try {
    await ev.prompt();
    const { outcome } = await ev.userChoice;
    if (outcome === 'accepted') {
      deferred = null;
      writeFlag(INSTALLED_KEY, 'true');
    }
    emit();
    return outcome;
  } catch {
    return 'unavailable';
  }
}

/** Aperta come app (icona sulla schermata Home). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const mm = (q: string) => !!window.matchMedia && window.matchMedia(q).matches;
    return (
      mm('(display-mode: standalone)') ||
      mm('(display-mode: fullscreen)') ||
      mm('(display-mode: minimal-ui)') ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}

/** Installata da questo browser (o detto dall'ospite). */
export const markedInstalled = (): boolean => readFlag(INSTALLED_KEY) === 'true';

/** Chrome per Android sa dire se l'app è già installata (manifest: related_applications). */
export async function detectInstalledApp(): Promise<boolean> {
  try {
    const nav = navigator as Navigator & { getInstalledRelatedApps?: () => Promise<unknown[]> };
    if (!nav.getInstalledRelatedApps) return false;
    const apps = await nav.getInstalledRelatedApps();
    return Array.isArray(apps) && apps.length > 0;
  } catch {
    return false;
  }
}

// ---- Su che telefono e con che browser ------------------------------------------

export type InstallGuide =
  /** Android con la finestra "Installa" pronta. */
  | { kind: 'native' }
  /** iPhone/iPad in Safari (o Chrome/Edge/Firefox per iPhone). */
  | { kind: 'ios'; modern: boolean; ipad: boolean; browser: 'safari' | 'chrome' | 'other' }
  /** Dentro un'app (Instagram, Facebook, TikTok…): prima si apre il browser. */
  | { kind: 'inapp'; ios: boolean }
  /** Android senza finestra pronta: i passi dal menu del browser. */
  | { kind: 'android'; browser: 'chrome' | 'samsung' | 'firefox' | 'other' }
  /** Computer o altro: niente da proporre da soli. */
  | { kind: 'none' };

const IN_APP_RE = /FBAN|FBAV|FB_IAB|Instagram|Line\/|LinkedInApp|Twitter|TikTok|musical_ly|Bytedance|Snapchat|Pinterest|GSA\/|; wv\)/i;

export function installGuide(
  ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
  maxTouchPoints: number = typeof navigator !== 'undefined' ? navigator.maxTouchPoints || 0 : 0,
  hasPrompt: boolean = canPromptInstall(),
): InstallGuide {
  const ipad = /iPad/i.test(ua) || (/Macintosh/i.test(ua) && maxTouchPoints > 1);
  const ios = /iPhone|iPod/i.test(ua) || ipad;
  if (IN_APP_RE.test(ua)) return { kind: 'inapp', ios };
  if (ios) {
    // Safari 26 tiene la versione del sistema ferma a 18 nel nome: conta "Version/26".
    const safariMajor = Number((/Version\/(\d+)/.exec(ua) || [])[1] || 0);
    const osMajor = Number((/OS (\d+)_/.exec(ua) || [])[1] || 0);
    const browser = /CriOS|EdgiOS/i.test(ua) ? 'chrome' : /FxiOS|OPiOS/i.test(ua) ? 'other' : 'safari';
    return { kind: 'ios', modern: Math.max(safariMajor, osMajor) >= 26, ipad, browser };
  }
  if (hasPrompt) return { kind: 'native' };
  if (/Android/i.test(ua)) {
    const browser = /SamsungBrowser/i.test(ua) ? 'samsung' : /Firefox/i.test(ua) ? 'firefox' : /Chrome/i.test(ua) ? 'chrome' : 'other';
    return { kind: 'android', browser };
  }
  return { kind: 'none' };
}

// ---- Quante volte proporla ------------------------------------------------------

interface InstallPrefs {
  /** Fino a quando non riproporla (ms). */
  snoozedUntil: number;
  /** Quante volte è uscita da sola. */
  shows: number;
  /** "L'ho già installata": mai più, e via il riquadro in Home. */
  never: boolean;
}

export function installPrefs(): InstallPrefs {
  try {
    const raw = JSON.parse(readFlag(PREFS_KEY) || '{}');
    return {
      snoozedUntil: Number(raw.snoozedUntil) || 0,
      shows: Number(raw.shows) || 0,
      never: raw.never === true,
    };
  } catch {
    return { snoozedUntil: 0, shows: 0, never: false };
  }
}

const savePrefs = (p: InstallPrefs) => {
  writeFlag(PREFS_KEY, JSON.stringify(p));
  emit();
};

export const snoozeInstall = (now: number = Date.now()) =>
  savePrefs({ ...installPrefs(), snoozedUntil: now + SNOOZE_DAYS * 24 * 60 * 60 * 1000 });
export const countAutoShow = () => savePrefs({ ...installPrefs(), shows: installPrefs().shows + 1 });
export const neverInstall = () => {
  writeFlag(INSTALLED_KEY, 'true');
  savePrefs({ ...installPrefs(), never: true });
};

/** Può uscire da sola adesso? (non installata, non rimandata, non già 3 volte). */
export function canAutoShowInstall(now: number = Date.now()): boolean {
  if (isStandalone() || markedInstalled()) return false;
  const p = installPrefs();
  return !p.never && p.shows < MAX_AUTO_SHOWS && now >= p.snoozedUntil;
}

/** Le pagine dove può uscire da sola: mai sopra check-in, pagamenti e link speciali. */
export function isCalmRoute(pathname: string, search: string = ''): boolean {
  if (/[?&](add|k|pwa-redirect|ref|session_id|token)=/.test(search)) return false;
  const calm = ['/', '/explore', '/partners', '/restaurants', '/events', '/my-stay', '/extras', '/offers/romantic'];
  return calm.includes(pathname) || pathname.startsWith('/cities/');
}
